import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';import { ArrowRight, ArrowLeft, CheckCircle2, User, Mail, Phone, MapPin, Ruler, FileText, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Form steps configuration
const STEPS = [
  { id: 1, title: 'Date Contact', icon: User },
  { id: 2, title: 'Tip Construcție', icon: FileText },
  { id: 3, title: 'Zonă Hidroizolație', icon: MapPin },
  { id: 4, title: 'Tip Izolație', icon: FileText },
  { id: 5, title: 'Detalii Proiect', icon: Ruler },
  { id: 6, title: 'Finalizare', icon: Send }
];

const CONSTRUCTION_TYPES = [
  { id: 'stadiu_proiect', label: 'Stadiu proiect' },
  { id: 'noua', label: 'Construcție nouă' },
  { id: 'refacere', label: 'Refacere / Renovare' }
];

const ZONES = [
  { id: 'acoperis', label: 'Acoperiș' },
  { id: 'terasa', label: 'Terasă' },
  { id: 'balcon', label: 'Balcon' },
  { id: 'pereti', label: 'Pereți' },
  { id: 'fundatii', label: 'Fundații' },
  { id: 'subsol', label: 'Subsol' },
  { id: 'peste_subsol', label: 'Peste subsol' },
  { id: 'rezervor_apa', label: 'Rezervor apă' },
  { id: 'grupuri_sanitare', label: 'Grupuri sanitare' }
];

const ISOLATION_TYPES = [
  { id: 'monostrat', label: 'Hidroizolație Terase Monostrat' },
  { id: 'dublustrat', label: 'Hidroizolație Terase Dublustrat' },
  { id: 'termo_necirculabile', label: 'Termo și Hidroizolație Terase Necirculabile' },
  { id: 'termo_circulabile', label: 'Termo și Hidroizolație Terase Circulabile' },
  { id: 'pvc_epdm', label: 'Termo și Hidroizolație PVC-EPDM' },
  { id: 'inversata', label: 'Termo și hidroizolație terasă inversată' },
  { id: 'vegetatie', label: 'Acoperiș cu vegetație / grădină' },
  { id: 'pereti_exterior', label: 'Hidroizolație și protecție exterioară pereți' },
  { id: 'poduri', label: 'Hidroizolație poduri și pasaje' },
  { id: 'epdm', label: 'Impermeabilizare cu membrane EPDM' }
];

const LOCATIONS = [
  { id: 'iasi', label: 'IAȘI' },
  { id: 'moldova', label: 'Zona MOLDOVEI' },
  { id: 'alta_zona', label: 'ALTE ZONE' }
];

const SURFACES = [
  { id: 'sub_50', label: 'Sub 50 mp' },
  { id: '50_100', label: '50 - 100 mp' },
  { id: '101_200', label: '101 - 200 mp' },
  { id: '201_400', label: '201 - 400 mp' },
  { id: '401_800', label: '401 - 800 mp' },
  { id: 'peste_800', label: 'Peste 800 mp' }
];

const QuoteRequest = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    constructionType: '',
    zones: [],
    isolationTypes: [],
    location: '',
    otherLocation: '',
    surface: '',
    additionalInfo: ''
  });

  useEffect(() => {
    // Pre-select service if provided in URL
    const serviciu = searchParams.get('serviciu');
    if (serviciu) {
      const mapping = {
        'hidroizolatie-terase-monostrat': 'monostrat',
        'hidroizolatie-terase-dublustrat': 'dublustrat',
        'termo-hidroizolatie-necirculabile': 'termo_necirculabile',
        'termo-hidroizolatie-circulabile': 'termo_circulabile',
        'hidroizolatie-pvc-epdm': 'pvc_epdm',
        'acoperis-verde': 'vegetatie',
        'hidroizolatie-fundatii': 'pereti_exterior',
        'impermeabilizare-epdm': 'epdm'
      };
      if (mapping[serviciu]) {
        setFormData(prev => ({
          ...prev,
          isolationTypes: [mapping[serviciu]]
        }));
      }
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      }
      return { ...prev, [field]: [...current, value] };
    });
  };

  const handleRadioChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.name || !formData.email || !formData.phone) {
          toast.error('Te rugăm să completezi toate câmpurile obligatorii');
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
          toast.error('Adresa de email nu este validă');
          return false;
        }
        return true;
      case 2:
        if (!formData.constructionType) {
          toast.error('Te rugăm să selectezi tipul de construcție');
          return false;
        }
        return true;
      case 3:
        if (formData.zones.length === 0) {
          toast.error('Te rugăm să selectezi cel puțin o zonă');
          return false;
        }
        return true;
      case 4:
        if (formData.isolationTypes.length === 0) {
          toast.error('Te rugăm să selectezi cel puțin un tip de izolație');
          return false;
        }
        return true;
      case 5:
        if (!formData.location || !formData.surface) {
          toast.error('Te rugăm să completezi locația și suprafața');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 6));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFillColor(0, 150, 136);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('IzoMAG', 20, 25);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Cerere Ofertă Hidroizolații', 20, 35);
    
    // Date
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(10);
    doc.text(`Data: ${new Date().toLocaleDateString('ro-RO')}`, pageWidth - 50, 25);
    
    let y = 55;
    
    // Section: Contact
    doc.setTextColor(0, 150, 136);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Date Contact', 20, y);
    y += 10;
    
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Nume: ${formData.name}`, 20, y); y += 7;
    doc.text(`Email: ${formData.email}`, 20, y); y += 7;
    doc.text(`Telefon: ${formData.phone}`, 20, y); y += 15;
    
    // Section: Project Details
    doc.setTextColor(0, 150, 136);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Detalii Proiect', 20, y);
    y += 10;
    
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    
    const constructionLabel = CONSTRUCTION_TYPES.find(c => c.id === formData.constructionType)?.label || '';
    doc.text(`Tip construcție: ${constructionLabel}`, 20, y); y += 7;
    
    const zonesLabels = formData.zones.map(z => ZONES.find(zone => zone.id === z)?.label).join(', ');
    doc.text(`Zone: ${zonesLabels}`, 20, y); y += 7;
    
    const isolationLabels = formData.isolationTypes.map(i => ISOLATION_TYPES.find(type => type.id === i)?.label).join(', ');
    const isolationText = doc.splitTextToSize(`Tipuri izolație: ${isolationLabels}`, pageWidth - 40);
    doc.text(isolationText, 20, y); y += isolationText.length * 7 + 3;
    
    const locationLabel = LOCATIONS.find(l => l.id === formData.location)?.label || '';
    const locationText = formData.location === 'alta_zona' ? formData.otherLocation : locationLabel;
    doc.text(`Locație: ${locationText}`, 20, y); y += 7;
    
    const surfaceLabel = SURFACES.find(s => s.id === formData.surface)?.label || '';
    doc.text(`Suprafață: ${surfaceLabel}`, 20, y); y += 15;
    
    // Additional Info
    if (formData.additionalInfo) {
      doc.setTextColor(0, 150, 136);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Informații Suplimentare', 20, y);
      y += 10;
      
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const additionalText = doc.splitTextToSize(formData.additionalInfo, pageWidth - 40);
      doc.text(additionalText, 20, y);
    }
    
    // Footer
    doc.setFillColor(240, 240, 240);
    doc.rect(0, 270, pageWidth, 30, 'F');
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(9);
    doc.text('IzoMAG - La noi găsești soluția corectă la prețul corect', pageWidth / 2, 280, { align: 'center' });
    doc.text('www.izomag.ro | contact@izomag.ro', pageWidth / 2, 287, { align: 'center' });
    
    return doc;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Generate PDF
      const pdf = generatePDF();
      const pdfBase64 = pdf.output('datauristring').split(',')[1];
      
      // Prepare data for API
      const submitData = {
        ...formData,
        constructionTypeLabel: CONSTRUCTION_TYPES.find(c => c.id === formData.constructionType)?.label,
        zonesLabels: formData.zones.map(z => ZONES.find(zone => zone.id === z)?.label),
        isolationTypesLabels: formData.isolationTypes.map(i => ISOLATION_TYPES.find(type => type.id === i)?.label),
        locationLabel: formData.location === 'alta_zona' ? formData.otherLocation : LOCATIONS.find(l => l.id === formData.location)?.label,
        surfaceLabel: SURFACES.find(s => s.id === formData.surface)?.label,
        pdfBase64: pdfBase64
      };
      
      // Submit to API
      const response = await axios.post(`${API}/quote-requests`, submitData);
      
      if (response.data.success) {
        // Download PDF
        pdf.save(`cerere-oferta-izomag-${new Date().getTime()}.pdf`);
        
        setIsSubmitted(true);
        toast.success('Cererea ta a fost trimisă cu succes!');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('A apărut o eroare. Te rugăm să încerci din nou.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const progress = (currentStep / 6) * 100;

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50">
        <SEO title="Vreau Oferta - Cerere de Pret" description="Solicita o oferta personalizata pentru lucrari de hidroizolatii. Completati formularul si va contactam in cel mai scurt timp." canonical="/vreau-oferta" />
        <Navbar />
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-card p-12"
              data-testid="success-message"
            >
              <CheckCircle2 className="w-24 h-24 text-green-500 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Cererea ta a fost trimisă!
              </h1>
              <p className="text-slate-600 mb-8">
                Îți mulțumim pentru cerere! Un consultant IzoMAG te va contacta în maxim 24 de ore.
                <br /><br />
                PDF-ul cu datele tale a fost descărcat automat.
              </p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={() => navigate('/servicii')}>
                  Vezi Serviciile
                </Button>
                <Button className="btn-primary" onClick={() => navigate('/')}>
                  Pagina Principală
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50" data-testid="quote-request-page">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Solicită Ofertă Gratuită
            </h1>
            <p className="text-slate-600">Completează formularul și primești oferta în maxim 24 de ore</p>
          </motion.div>

          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              {STEPS.map((step) => (
                <div
                  key={step.id}
                  className={`flex flex-col items-center ${currentStep >= step.id ? 'text-cyan-600' : 'text-slate-400'}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                    currentStep > step.id ? 'bg-green-500 text-white' :
                    currentStep === step.id ? 'bg-cyan-600 text-white' :
                    'bg-slate-200 text-slate-500'
                  }`}>
                    {currentStep > step.id ? <CheckCircle2 className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
                  </div>
                  <span className="text-xs hidden md:block">{step.title}</span>
                </div>
              ))}
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Form Steps */}
          <div className="glass-card p-8">
            <AnimatePresence mode="wait">
              {/* Step 1: Contact */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Date de contact
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Nume complet *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Ion Popescu"
                        className="mt-1"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="email@exemplu.ro"
                        className="mt-1"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Telefon *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="0722 123 456"
                        className="mt-1"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Construction Type */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Care este tipul de construcție?
                  </h2>
                  <div className="space-y-3">
                    {CONSTRUCTION_TYPES.map((type) => (
                      <label
                        key={type.id}
                        className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition ${
                          formData.constructionType === type.id
                            ? 'border-cyan-500 bg-cyan-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`construction-${type.id}`}
                      >
                        <input
                          type="radio"
                          name="constructionType"
                          value={type.id}
                          checked={formData.constructionType === type.id}
                          onChange={() => handleRadioChange('constructionType', type.id)}
                          className="sr-only"
                        />
                        <span className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                          formData.constructionType === type.id ? 'border-cyan-500' : 'border-slate-300'
                        }`}>
                          {formData.constructionType === type.id && (
                            <span className="w-3 h-3 rounded-full bg-cyan-500" />
                          )}
                        </span>
                        <span className="font-medium text-slate-900">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 3: Zones */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Care este zona pe care vrei să o hidroizolezi?
                  </h2>
                  <p className="text-slate-500 mb-6">Poți selecta mai multe opțiuni</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {ZONES.map((zone) => (
                      <label
                        key={zone.id}
                        className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition ${
                          formData.zones.includes(zone.id)
                            ? 'border-cyan-500 bg-cyan-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`zone-${zone.id}`}
                      >
                        <Checkbox
                          checked={formData.zones.includes(zone.id)}
                          onCheckedChange={() => handleCheckboxChange('zones', zone.id)}
                          className="mr-2"
                        />
                        <span className="text-sm font-medium text-slate-900">{zone.label}</span>
                      </label>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 4: Isolation Types */}
              {currentStep === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Ce fel de tip de izolație te interesează?
                  </h2>
                  <p className="text-slate-500 mb-6">Poți selecta mai multe opțiuni</p>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {ISOLATION_TYPES.map((type) => (
                      <label
                        key={type.id}
                        className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition ${
                          formData.isolationTypes.includes(type.id)
                            ? 'border-cyan-500 bg-cyan-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`isolation-${type.id}`}
                      >
                        <Checkbox
                          checked={formData.isolationTypes.includes(type.id)}
                          onCheckedChange={() => handleCheckboxChange('isolationTypes', type.id)}
                          className="mr-2"
                        />
                        <span className="text-sm font-medium text-slate-900">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 5: Project Details */}
              {currentStep === 5 && (
                <motion.div
                  key="step5"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Detalii proiect
                  </h2>
                  
                  <div className="space-y-6">
                    <div>
                      <Label className="mb-3 block">Unde se află construcția? *</Label>
                      <div className="grid grid-cols-3 gap-3">
                        {LOCATIONS.map((loc) => (
                          <label
                            key={loc.id}
                            className={`flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition ${
                              formData.location === loc.id
                                ? 'border-cyan-500 bg-cyan-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            data-testid={`location-${loc.id}`}
                          >
                            <input
                              type="radio"
                              name="location"
                              value={loc.id}
                              checked={formData.location === loc.id}
                              onChange={() => handleRadioChange('location', loc.id)}
                              className="sr-only"
                            />
                            <span className="text-sm font-medium text-slate-900">{loc.label}</span>
                          </label>
                        ))}
                      </div>
                      {formData.location === 'alta_zona' && (
                        <Input
                          name="otherLocation"
                          value={formData.otherLocation}
                          onChange={handleInputChange}
                          placeholder="Specifică zona..."
                          className="mt-3"
                          data-testid="other-location"
                        />
                      )}
                    </div>

                    <div>
                      <Label className="mb-3 block">Care este suprafața proiectului? *</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {SURFACES.map((surface) => (
                          <label
                            key={surface.id}
                            className={`flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition ${
                              formData.surface === surface.id
                                ? 'border-cyan-500 bg-cyan-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            data-testid={`surface-${surface.id}`}
                          >
                            <input
                              type="radio"
                              name="surface"
                              value={surface.id}
                              checked={formData.surface === surface.id}
                              onChange={() => handleRadioChange('surface', surface.id)}
                              className="sr-only"
                            />
                            <span className="text-sm font-medium text-slate-900">{surface.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="additionalInfo">Informații suplimentare (opțional)</Label>
                      <Textarea
                        id="additionalInfo"
                        name="additionalInfo"
                        value={formData.additionalInfo}
                        onChange={handleInputChange}
                        placeholder="Ce alte detalii dorești să ne comunici?"
                        className="mt-1"
                        rows={4}
                        data-testid="additional-info"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 6: Summary */}
              {currentStep === 6 && (
                <motion.div
                  key="step6"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Verifică datele și trimite cererea
                  </h2>
                  
                  <div className="space-y-4 bg-slate-50 rounded-xl p-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-slate-500">Nume</span>
                        <p className="font-medium">{formData.name}</p>
                      </div>
                      <div>
                        <span className="text-sm text-slate-500">Email</span>
                        <p className="font-medium">{formData.email}</p>
                      </div>
                      <div>
                        <span className="text-sm text-slate-500">Telefon</span>
                        <p className="font-medium">{formData.phone}</p>
                      </div>
                      <div>
                        <span className="text-sm text-slate-500">Tip construcție</span>
                        <p className="font-medium">{CONSTRUCTION_TYPES.find(c => c.id === formData.constructionType)?.label}</p>
                      </div>
                    </div>
                    
                    <div>
                      <span className="text-sm text-slate-500">Zone</span>
                      <p className="font-medium">{formData.zones.map(z => ZONES.find(zone => zone.id === z)?.label).join(', ')}</p>
                    </div>
                    
                    <div>
                      <span className="text-sm text-slate-500">Tipuri izolație</span>
                      <p className="font-medium">{formData.isolationTypes.map(i => ISOLATION_TYPES.find(type => type.id === i)?.label).join(', ')}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-slate-500">Locație</span>
                        <p className="font-medium">
                          {formData.location === 'alta_zona' ? formData.otherLocation : LOCATIONS.find(l => l.id === formData.location)?.label}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-slate-500">Suprafață</span>
                        <p className="font-medium">{SURFACES.find(s => s.id === formData.surface)?.label}</p>
                      </div>
                    </div>
                    
                    {formData.additionalInfo && (
                      <div>
                        <span className="text-sm text-slate-500">Informații suplimentare</span>
                        <p className="font-medium">{formData.additionalInfo}</p>
                      </div>
                    )}
                  </div>

                  <p className="text-sm text-slate-500 mt-4 text-center">
                    După trimitere, vei primi un PDF cu toate datele completate.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                data-testid="prev-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Înapoi
              </Button>
              
              {currentStep < 6 ? (
                <Button onClick={nextStep} className="btn-primary" data-testid="next-btn">
                  Continuă
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={isSubmitting}
                  data-testid="submit-btn"
                >
                  {isSubmitting ? 'Se trimite...' : 'Trimite Cererea'}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteRequest;
